import {
	ArrowUturnLeftIcon,
	ClipboardDocumentIcon,
	SpeakerWaveIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { ANALYTICS_EVENTS } from "./constants";
import styles from "./MessageActions.module.css";

interface MessageActionsProps {
	content: string;
}

export function MessageActions({ content }: MessageActionsProps) {
	const [isSpeaking, setIsSpeaking] = useState(false);
	const { logEvent } = useAnalytics();
	const { t } = useClientTranslations("chat");

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(content);
			toast.success(t("toast.copiedToClipboard"));
			logEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_COPIED, undefined, {
				content_length: content.length,
			});
		} catch (error) {
			console.error("Failed to copy:", error);
			toast.error(t("toast.failedToCopy"));
		}
	};

	const handleReadAloud = () => {
		if (isSpeaking) {
			window.speechSynthesis.cancel();
			setIsSpeaking(false);
			return;
		}

		const utterance = new SpeechSynthesisUtterance(content);
		utterance.onend = () => setIsSpeaking(false);
		utterance.onerror = () => {
			setIsSpeaking(false);
			toast.error(t("toast.speechSynthesisFailed"));
		};

		window.speechSynthesis.speak(utterance);
		setIsSpeaking(true);
		logEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_READ_ALOUD, undefined, {
			content_length: content.length,
		});
	};

	return (
		<div className={styles.container}>
			<button
				type="button"
				onClick={handleReadAloud}
				className={styles.actionButton}
				aria-label={isSpeaking ? "Stop reading" : "Read aloud"}
			>
				<SpeakerWaveIcon className={styles.icon} />
			</button>

			<button
				type="button"
				onClick={handleCopy}
				className={styles.actionButton}
				aria-label="Copy"
			>
				<ClipboardDocumentIcon className={styles.icon} />
			</button>

			<button
				type="button"
				onClick={() => {
					logEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_REPLY_CLICKED, undefined, {
						content_length: content.length,
					});
				}}
				className={styles.actionButton}
				aria-label="Reply"
			>
				<ArrowUturnLeftIcon className={styles.icon} />
			</button>
		</div>
	);
}
